import "./PromotionLabelList.scss";

import React from "react";
import { FormattedMessage } from "react-intl";
import { Label } from "react-bootstrap";
import { PromotionRemainingDaysLabel } from "./PromotionRemainingDaysLabel";
import { FlashSalesLabel } from "./FlashSalesLabel";
import ErrorBoundary from "../ErrorBoundary";
import { getFilteredMessages, jsxWithIntl } from "../../i18n/IntlRootMixin";
import { PromotionLabel, SellingPriceDnrInfo } from "../../types/PriceAndPromotionInfo";
import {
    SellingPriceDnRInfo,
    PromotionLabel as EvaluateArticleNewPromotionLabel
} from "../../types/EvaluateArticleNew";
import { withReactAdapter } from "../../module-federation/withReactAdapter";

const promotionTypes = new Map<string, string>([
    ["dnr", "EX_PROMOTION_DNR"],
    ["web", "EX_PROMOTION_WEB"],
    ["flash", "EX_PROMOTION_FLASH"],
    ["3for2", "EX_PROMOTION_3FOR2"],
    ["freegift", "EX_PROMOTION_FREEGIFT"],
    ["promotion", "EX_PROMOTION_PROMOTION"],
    ["good_deal", "EX_PROMOTION_GOOD_DEAL"],
    ["endofseason", "EX_PROMOTION_ENDOFSEASON"],
    ["freedelivery", "EX_PROMOTION_FREEDELIVERY"],
    ["hamperbundle", "EX_PROMOTION_HAMPERBUNDLE"],
    ["we_exclusive", "EX_PROMOTION_WEB_EXCLUSIVE"]
]);

const specialStyles = new Map<string, string>([["good_deal", "label-good-deal"]]);

const phraseProject = "EXPLORE";
const neededTranslationMessages = [
    "SD_ARTICLECARD_PROMOTION_FLASH_COUNTER",
    "SD_ARTICLECARD_PROMOTION_REMAINING_DAYS",
    "SD_ARTICLECARD_PROMOTION_REMAINING_DAYS_ZERO",
    "SD_ARTICLECARD_PROMOTION_DAYS_UNTIL_START",
    "SD_ARTICLECARD_PROMOTION_DAYS_UNTIL_START_ZERO",
    "EX_LABEL_RECENTLY_ORDERED_WITH_DATE",
    "EX_LABEL_RECENTLY_ORDERED",
    "EX_PROMOTION_DNR",
    "EX_PROMOTION_WEB",
    "EX_PROMOTION_FLASH",
    "EX_PROMOTION_3FOR2",
    "EX_PROMOTION_FREEGIFT",
    "EX_PROMOTION_PROMOTION",
    "EX_PROMOTION_GOOD_DEAL",
    "EX_PROMOTION_ENDOFSEASON",
    "EX_PROMOTION_FREEDELIVERY",
    "EX_PROMOTION_HAMPERBUNDLE",
    "EX_PROMOTION_WEB_EXCLUSIVE"
];

const filteredMessages = getFilteredMessages(phraseProject, neededTranslationMessages);

export type PromotionLabelListProps = {
    promotionLabels: { [p: string]: PromotionLabel } | Record<string, EvaluateArticleNewPromotionLabel> | undefined;
    dnrInfo: { [x: string]: SellingPriceDnrInfo } | Record<string, SellingPriceDnRInfo> | undefined;
    flashSalesCounter: number | null | undefined;
    recentlyOrdered?: boolean | null;
    maxPromotionLabelsShown: number;
    recentlyOrderedDateString?: string;
    shouldRenderRowWise?: boolean;
};

export function PromotionLabelListUnwrapped({
    promotionLabels,
    dnrInfo,
    flashSalesCounter,
    recentlyOrdered,
    maxPromotionLabelsShown,
    recentlyOrderedDateString,
    shouldRenderRowWise = false
}: PromotionLabelListProps) {
    const promotions = Object.values(promotionLabels || []);
    const sortedPromotions = promotions.sort((prom1, prom2) => {
        if (prom1.displayPriority && prom2.displayPriority) {
            return prom1.displayPriority > prom2.displayPriority ? -1 : 1;
        }
        return prom1.displayPriority ? -1 : 1;
    });

    const labels: Array<React.ReactNode> = [];
    labels.push(
        ...sortedPromotions.map((promotion) => {
            const type = promotion.type.toLowerCase();
            if (promotionTypes.has(type)) {
                const translationKey = promotionTypes.get(type);
                const optionalSpecialStyles = specialStyles.get(type) || "";
                const flashSalesLabel = type === "flash" && (
                    <FlashSalesLabel dnrInfos={dnrInfo} flashSalesCounter={flashSalesCounter} />
                );
                const promotionRemainingDaysLabel = !flashSalesLabel && (
                    <PromotionRemainingDaysLabel promotionStart={promotion?.start} promotionEnd={promotion?.end} />
                );

                return (
                    <div key={translationKey}>
                        <Label className={"label-rectangular label-promotion " + optionalSpecialStyles}>
                            <FormattedMessage id={translationKey} />
                        </Label>
                        {promotionRemainingDaysLabel}
                        {flashSalesLabel}
                    </div>
                );
            } else {
                return null;
            }
        })
    );

    if (recentlyOrdered) {
        const croppedLabelsList = labels.slice(0, maxPromotionLabelsShown - 1);
        croppedLabelsList.push(
            <Label className="label-rectangular label-recently-ordered" key="RECENTLY_ORDERED">
                {recentlyOrderedDateString ? (
                    <FormattedMessage
                        id="EX_LABEL_RECENTLY_ORDERED_WITH_DATE"
                        values={{
                            date: recentlyOrderedDateString
                        }}
                    />
                ) : (
                    <FormattedMessage id="EX_LABEL_RECENTLY_ORDERED" />
                )}
            </Label>
        );
        return (
            <div className={"sd-shared--promotion-labels " + (shouldRenderRowWise ? "promotion-labels-row" : "")}>
                {croppedLabelsList}
            </div>
        );
    }

    return (
        <div className={"sd-shared--promotion-labels " + (shouldRenderRowWise ? "promotion-labels-row" : "")}>
            {labels.slice(0, maxPromotionLabelsShown)}
        </div>
    );
}

export function PromotionLabelList(props: PromotionLabelListProps) {
    return (
        <ErrorBoundary>
            {jsxWithIntl(<PromotionLabelListUnwrapped {...props} />, phraseProject, filteredMessages)}
        </ErrorBoundary>
    );
}

export default withReactAdapter(PromotionLabelList);
